import { AccessToken, isTokenValid } from 'hooks/auth/Token';
import React, { createContext, useCallback, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface AuthContextType {
    token: AccessToken | null;
    setToken: React.Dispatch<React.SetStateAction<AccessToken | null>>;
    handleUnauthenticated: () => void;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const loadToken = (): AccessToken | null => {
    try {
        const storedToken = localStorage.getItem('accessToken');

        if (!storedToken) {
            return null;
        }

        const token = JSON.parse(storedToken) as AccessToken;
        const normalizedToken = { ...token, expires: new Date(token.expires) };
        if (!isTokenValid(normalizedToken, false)) {
             localStorage.removeItem('accessToken');
             return null;
        }
        return normalizedToken;
    } catch {
        localStorage.removeItem('accessToken');
        return null;
    }
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [token, setToken] = useState<AccessToken | null>(loadToken);

    const clearToken = useCallback(() => {
        setToken(null);
    }, []);

    const handleUnauthenticated = useCallback(() => {
        if (!['/login', '/logout'].includes(location.pathname)) {
            sessionStorage.setItem('authReturnPath', `${location.pathname}${location.search}${location.hash}`);
        }

        clearToken();
        navigate('/login', { replace: true });
    }, [location, clearToken, navigate]);

    const signOut = useCallback(() => {
        sessionStorage.removeItem('authReturnPath');
        clearToken();
    }, [clearToken]);

    useEffect(() => {
        if (token) {
            localStorage.setItem('accessToken', JSON.stringify(token));
        } else {
            localStorage.removeItem('accessToken');
        }
    }, [token]);

    const contextValue = useMemo(
        () => ({token, setToken, handleUnauthenticated, signOut}),
        [token, handleUnauthenticated, signOut]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};